import type { TokenTools } from '@ardrive/turbo-sdk';
import { delay } from 'es-toolkit/promise';
import type { Either } from 'fp-ts/Either';
import { isLeftResult, isRightResult } from '../../../../lib/trpcTypeGuards';
import { TRPC_URL } from '../../../../lib/config';
import { createAndSubmitArDriveTopUpTx } from '../../../ardriveTopUp';
import {
  BackendProxyConnection,
  resolveProxyEndpoint,
} from '../../../BackendProxyConnection';
import { addBreadcrumb, captureAndReportError } from '../../../errorReporting';
import { SOLANA_CLUSTER } from '../../../solanaUtils';
import type { ProviderTrpcClient, WalletAdapter } from '../../types';

export function createSolanaTokenTools(options: {
  trpcClient: ProviderTrpcClient;
  walletAdapter: WalletAdapter;
}): TokenTools {
  const { trpcClient, walletAdapter } = options;

  if (!walletAdapter.publicKey) {
    throw new Error('Wallet adapter is required for Solana token tools');
  }

  const unwrapRpc = (resp: unknown) => {
    if (resp && typeof resp === 'object' && '_tag' in resp) {
      const tagged = resp as Either<{ message?: string }, unknown>;
      if (isRightResult(tagged)) {
        return tagged.right;
      }

      if (isLeftResult(tagged)) {
        throw new Error(tagged.left?.message ?? 'Solana RPC error');
      }
    }

    return resp;
  };

  const rpcCall = async (method: string, params: unknown[]) => {
    const raw = await trpcClient.solanaRpc.proxy.mutate({
      jsonrpc: '2.0',
      id: Date.now(),
      method,
      params,
      network: SOLANA_CLUSTER,
    });
    const resp = unwrapRpc(raw) as {
      result?: unknown;
      error?: { message?: string };
    };

    if (!resp || typeof resp !== 'object') {
      throw new Error(`RPC ${method} failed: empty response`);
    }

    if (resp.error) {
      throw new Error(
        `RPC ${method} error: ${resp.error.message ?? 'Unknown error'}`,
      );
    }

    return resp.result;
  };

  const getLatestBlockhash = async () => {
    const result = await rpcCall('getLatestBlockhash', []);
    const value =
      (
        result as {
          value?: { blockhash?: string; lastValidBlockHeight?: number };
        }
      )?.value ??
      (result as { blockhash?: string; lastValidBlockHeight?: number });

    const blockhash = value?.blockhash;
    const lastValidBlockHeight = value?.lastValidBlockHeight;

    if (!blockhash || !lastValidBlockHeight) {
      captureAndReportError('getLatestBlockhash missing value', {
        result,
        value,
      });
      throw new Error('Failed to fetch blockhash: missing value');
    }

    try {
      console.log(
        '[ArDrive] getLatestBlockhash response',
        JSON.stringify({ blockhash, lastValidBlockHeight }),
      );
    } catch {}

    return {
      blockhash,
      lastValidBlockHeight,
    };
  };

  const sendRawTransaction = async (serializedTx: Buffer | Uint8Array) => {
    const base64Tx = Buffer.isBuffer(serializedTx)
      ? serializedTx.toString('base64')
      : Buffer.from(serializedTx).toString('base64');
    const result = await rpcCall('sendTransaction', [
      base64Tx,
      { encoding: 'base64' },
    ]);

    if (!result) {
      throw new Error('Failed to send transaction: missing result');
    }

    console.log('[ArDrive] sent transaction', { signature: result });
    return result as string;
  };

  const connection = new BackendProxyConnection(
    resolveProxyEndpoint(TRPC_URL),
    (request) =>
      trpcClient.solanaRpc.proxy.mutate({
        ...request,
        network: SOLANA_CLUSTER,
      }),
    {
      commitment: 'confirmed',
      disableRetryOnRateLimit: true,
    },
  );

  const pollSignature = async (signature: string) => {
    const maxAttempts = 60;
    const delayMs = 5_000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const result = await rpcCall('getSignatureStatuses', [[signature]]);
      const status = (
        result as {
          value?: Array<{ err: unknown; confirmationStatus: string | null }>;
        }
      )?.value?.[0];

      if (status?.err) {
        console.log('[ArDrive] signature status error', {
          signature,
          status,
        });
        throw new Error('Transaction failed on-chain');
      }

      if (status && status.confirmationStatus === 'finalized') {
        console.log('[ArDrive] signature finalized', { signature });
        await delay(20_000);
        return;
      }

      console.log('[ArDrive] waiting for confirmation', {
        signature,
        attempt,
        status,
      });

      await delay(delayMs);
    }

    throw new Error('Transaction was not confirmed in time');
  };

  return {
    createAndSubmitTx: async ({
      target,
      tokenAmount,
      turboCreditDestinationAddress,
    }) =>
      createAndSubmitArDriveTopUpTx({
        connection,
        walletAdapter,
        target,
        tokenAmount,
        turboCreditDestinationAddress,
        getLatestBlockhash,
        sendRawTransaction,
        onRetry: ({ attempt, errorMessage, usedWalletSendTransaction }) => {
          addBreadcrumb(
            'Retrying ArDrive top-up with fresh blockhash',
            'ardrive.topup',
            {
              attempt,
              errorMessage,
              usedWalletSendTransaction,
            },
          );
        },
      }),
    pollTxAvailability: async ({ txId }: { txId: string }) => {
      await pollSignature(txId);
    },
  };
}
